// src/components/compliance/modals/ScoreDetailsModal.jsx

import React from "react";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";

function formatSignedNumber(value, digits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric > 0 ? `+${numeric.toFixed(digits)}` : numeric.toFixed(digits);
}

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

const itemCardStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.42)",
  display: "grid",
  gap: 10,
};

const metricLabelStyle = {
  color: "#f8fafc",
  fontSize: 12,
  fontWeight: 800,
};

const metricValueStyle = {
  color: "#60a5fa",
  fontSize: 24,
  lineHeight: 1.1,
  marginTop: 4,
};

const metricSubStyle = {
  color: "#bfdbfe",
  fontSize: 12,
  marginTop: 4,
  lineHeight: 1.35,
};

export default function ScoreDetailsModal({
  open,
  heroMetrics,
  riskTone,
  onClose,
}) {
  if (!open) return null;

  const rawScoreColor = riskTone === "low" ? "#34d399" : riskTone === "medium" ? "#fbbf24" : "#f87171";
  const operationalScoreColor =
    heroMetrics.operationalScore >= 95
      ? "#34d399"
      : heroMetrics.operationalScore >= 85
      ? "#fbbf24"
      : "#f87171";

  return (
    <ModalShell
      open={open}
      ariaLabel="Score details"
      eyebrow="OASYS · SCORE DETAILS"
      title="Raw Score vs Operational Score"
      subtitle="Raw score preserves the auditor-safe source posture. Operational score separates approved, evidence-backed accepted risk from active unresolved work."
      onClose={onClose}
      maxWidth="920px"
      accentBorder="rgba(74, 222, 128, 0.45)"
      zIndex={145}
    >
      <div style={metricGridStyle}>
        <MetricTile
          label="Raw Score"
          value={`${heroMetrics.latestScore.toFixed(2)}%`}
          sub="includes all findings"
          color={rawScoreColor}
        />
        <MetricTile
          label="Operational Score"
          value={`${heroMetrics.operationalScore.toFixed(2)}%`}
          sub="accepted risk separated"
          color={operationalScoreColor}
        />
        <MetricTile
          label="Raw Delta"
          value={formatSignedNumber(heroMetrics.rawScoreDelta, 2)}
          sub={
            heroMetrics.previousRawScore !== null
              ? `previous raw ${heroMetrics.previousRawScore.toFixed(2)}%`
              : "previous unavailable"
          }
          color={heroMetrics.rawScoreDelta > 0 ? "#34d399" : heroMetrics.rawScoreDelta < 0 ? "#f87171" : "#60a5fa"}
        />
        <MetricTile
          label="Operational Delta"
          value={formatSignedNumber(heroMetrics.operationalScoreDelta, 2)}
          sub={
            heroMetrics.previousOperationalScore !== null
              ? `previous op ${heroMetrics.previousOperationalScore.toFixed(2)}%`
              : "previous unavailable"
          }
          color={
            heroMetrics.operationalScoreDelta > 0
              ? "#34d399"
              : heroMetrics.operationalScoreDelta < 0
              ? "#f87171"
              : "#60a5fa"
          }
        />
      </div>

      <ModalSection
        title="Score Burden Details"
        description="Audit-safe score inputs and accepted-risk separation used for posture interpretation."
        badge="live posture"
        badgeTone="ok"
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
          <div style={itemCardStyle}>
            <div style={metricLabelStyle}>Raw Fail Findings</div>
            <div style={{ ...metricValueStyle, color: heroMetrics.rawFailCount > 0 ? "#f87171" : "#34d399" }}>
              {heroMetrics.rawFailCount}
            </div>
            <div style={metricSubStyle}>from source posture</div>
          </div>
          <div style={itemCardStyle}>
            <div style={metricLabelStyle}>Active Findings</div>
            <div style={{ ...metricValueStyle, color: heroMetrics.activeFailCount > 0 ? "#f87171" : "#34d399" }}>
              {heroMetrics.activeFailCount}
            </div>
            <div style={metricSubStyle}>unresolved findings</div>
          </div>
          <div style={itemCardStyle}>
            <div style={metricLabelStyle}>Accepted Risk</div>
            <div style={{ ...metricValueStyle, color: heroMetrics.acceptedRiskFindings > 0 ? "#fbbf24" : "#34d399" }}>
              {heroMetrics.acceptedRiskFindings}
            </div>
            <div style={metricSubStyle}>approved exceptions</div>
          </div>
          <div style={itemCardStyle}>
            <div style={metricLabelStyle}>Raw Nonpassing</div>
            <div style={{ ...metricValueStyle, color: heroMetrics.rawNonpassingFindings > 0 ? "#fbbf24" : "#34d399" }}>
              {heroMetrics.rawNonpassingFindings}
            </div>
            <div style={metricSubStyle}>fail + error + unknown</div>
          </div>
        </div>

        <article style={itemCardStyle}>
          <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 17, lineHeight: 1.25 }}>
            Audit interpretation
          </h4>
          <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            Raw Score is the official source posture and includes every detected finding. Operational Score is the
            active-work posture after separating approved, evidence-backed accepted risk. Accepted risk is not hidden
            or treated as a pass; it remains visible, documented, reviewable, and time-bound.
          </p>
          <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {heroMetrics.scoreAdjustmentReason}
          </p>
        </article>
      </ModalSection>
    </ModalShell>
  );
}
